/**
 * useStreamingVoice Hook
 *
 * Coordinates voice missions with the streaming service.
 * Listens for voice.room_ready events and connects to LiveKit,
 * then sends voice.user_joined to trigger mission dispatch.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import {
  Room,
  RoomEvent,
  Track,
  Participant,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  LocalAudioTrack,
  ConnectionState,
  createLocalAudioTrack,
} from 'livekit-client'
import type { VoiceRoomInfo } from './useStreamingMissionChat'

export type StreamingVoiceState = 'idle' | 'connecting' | 'in_call' | 'ended' | 'error'

interface UseStreamingVoiceOptions {
  voiceRoom: VoiceRoomInfo | null
  onUserJoined?: () => void
  echoCancellation?: boolean
  noiseSuppression?: boolean
  autoGainControl?: boolean
}

interface UseStreamingVoiceResult {
  voiceState: StreamingVoiceState
  isMuted: boolean
  isRemoteSpeaking: boolean
  /** True when the local user's mic is picking up speech (LiveKit VAD) */
  isLocalSpeaking: boolean
  error: string | null
  duration: number
  connect: () => Promise<void>
  disconnect: () => void
  toggleMute: () => void
}

export function useStreamingVoice(options: UseStreamingVoiceOptions): UseStreamingVoiceResult {
  const {
    voiceRoom,
    onUserJoined,
    echoCancellation = true,
    noiseSuppression = true,
    autoGainControl = true,
  } = options

  const [voiceState, setVoiceState] = useState<StreamingVoiceState>('idle')
  const [isMuted, setIsMuted] = useState(false)
  const [isRemoteSpeaking, setIsRemoteSpeaking] = useState(false)
  const [isLocalSpeaking, setIsLocalSpeaking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [duration, setDuration] = useState(0)

  const roomRef = useRef<Room | null>(null)
  const localTrackRef = useRef<LocalAudioTrack | null>(null)
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)

  const cleanup = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
    if (localTrackRef.current) {
      localTrackRef.current.stop()
      localTrackRef.current = null
    }
    if (roomRef.current) {
      roomRef.current.disconnect()
      roomRef.current = null
    }
    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null
      audioElementRef.current = null
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanup()
  }, [cleanup])

  const connect = useCallback(async () => {
    if (!voiceRoom) {
      setError('No voice room credentials available')
      return
    }

    try {
      setVoiceState('connecting')
      setError(null)
      setDuration(0)

      const room = new Room({
        audioCaptureDefaults: {
          echoCancellation,
          noiseSuppression,
          autoGainControl,
          sampleRate: 16000,
          channelCount: 1,
        },
      })
      roomRef.current = room

      // Track subscriptions
      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, _participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Audio) {
          const audioElement = document.createElement('audio')
          audioElement.autoplay = true
          audioElement.setAttribute('playsinline', '')
          track.attach(audioElement)
          audioElementRef.current = audioElement
        }
      })

      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Audio) {
          track.detach()
        }
      })

      // Speaking detection via LiveKit server-side VAD
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        const localIdentity = room.localParticipant?.identity
        setIsRemoteSpeaking(speakers.some(s => s.identity !== localIdentity))
        setIsLocalSpeaking(speakers.some(s => s.identity === localIdentity))
      })

      room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        switch (state) {
          case ConnectionState.Connected:
            setVoiceState('in_call')
            durationIntervalRef.current = setInterval(() => {
              setDuration(d => d + 1)
            }, 1000)
            break
          case ConnectionState.Reconnecting:
            // Keep in_call state during reconnect
            break
          case ConnectionState.Disconnected:
            setVoiceState('ended')
            cleanup()
            break
        }
      })

      room.on(RoomEvent.Disconnected, () => {
        setVoiceState('ended')
        cleanup()
      })

      room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        if (participant.identity.includes('agent') || participant.identity.includes('Agent')) {
          setVoiceState('ended')
          cleanup()
        }
      })

      // Connect to LiveKit room using streaming-service credentials
      await room.connect(voiceRoom.serverUrl, voiceRoom.participantToken)

      // Create and publish local audio track
      const localTrack = await createLocalAudioTrack({
        echoCancellation,
        noiseSuppression,
        autoGainControl,
        sampleRate: 16000,
        channelCount: 1,
      })
      localTrackRef.current = localTrack
      await room.localParticipant.publishTrack(localTrack)

      // Notify streaming service that user has connected
      onUserJoined?.()

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect to voice room')
      setVoiceState('error')
      cleanup()
    }
  }, [voiceRoom, echoCancellation, noiseSuppression, autoGainControl, onUserJoined, cleanup])

  const disconnect = useCallback(() => {
    cleanup()
    setVoiceState('ended')
    setDuration(0)
  }, [cleanup])

  const toggleMute = useCallback(() => {
    if (localTrackRef.current) {
      if (isMuted) {
        localTrackRef.current.unmute()
      } else {
        localTrackRef.current.mute()
      }
      setIsMuted(!isMuted)
    }
  }, [isMuted])

  return {
    voiceState,
    isMuted,
    isRemoteSpeaking,
    isLocalSpeaking,
    error,
    duration,
    connect,
    disconnect,
    toggleMute,
  }
}
